import { Box, Stack, Tooltip } from "@mui/material";
import {
  Activity,
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
} from "generated-graphql/graphql";
import { useActivityDependencies } from "hooks/useActivityDependencies";
import { CompleteActivity } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities/ActivityBottomToolbar/CompleteActivity";
import { Requeue } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities/ActivityBottomToolbar/Requeue";
import { SetToManual } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities/ActivityBottomToolbar/SetToManual";

import { useCohortDetails } from "../../useCohortDetails";

type CohortActivityBottomToolbarProps = {
  activity: Activity;
  allActivities?: Activity[];
};

export function CohortActivityBottomToolbar({
  activity,
  allActivities = [],
}: CohortActivityBottomToolbarProps) {
  const { data } = useCohortDetails();

  // Check if activity is a staff activity and has its dependencies met
  const { canComplete, tooltipMessage } = useActivityDependencies(activity, allActivities);

  // Original logic: activity is "current" if not in these statuses
  const isNotTerminalStatus = ![
    ActivityStatus.NotStarted,
    ActivityStatus.StaffCompleted,
    ActivityStatus.Completed,
  ].includes(activity?.status);

  // NEW: For staff activities, enable completion if:
  // 1. Activity is in a completable status (original logic), OR
  // 2. Activity is a staff activity with dependencies met (even if NotStarted)
  const isStaffActivity = activity?.type === ActivityType.Staff;
  const canCompleteOutOfOrder =
    isStaffActivity &&
    activity?.status !== ActivityStatus.Completed &&
    activity?.status !== ActivityStatus.StaffCompleted;

  const showCompleteButton = isNotTerminalStatus || canCompleteOutOfOrder;
  const isCompleteButtonDisabled = isStaffActivity && !canComplete;

  const canRequeue = isNotTerminalStatus && activity?.status === ActivityStatus.Error;
  const skipResumeDialog = activity?.executionPlan === ExecutionPlanType.Human;

  const tags = data?.cohort?.currentWorkflow?.tags?.map((t) => t.name).join(", ");

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: "space-between",
        alignItems: "flex-start",
        mt: 2,
      }}
    >
      <Stack direction="row" spacing={1}>
        <SetToManual activity={activity} refetchQueries={["GetCohort"]} />
        {canRequeue && (
          <Requeue
            activity={activity}
            context="cohort"
            refetchQueries={["GetCohort"]}
            showWarningPanel={!skipResumeDialog}
          />
        )}
      </Stack>
      {showCompleteButton &&
        (() => {
          const completeButton = (
            <CompleteActivity
              activity={activity}
              context="cohort"
              tags={tags}
              refetchQueries={["GetCohort"]}
              showWarningPanel={
                activity?.status === ActivityStatus.Error ||
                activity.executionPlan === ExecutionPlanType.Automation
              }
              disabled={isCompleteButtonDisabled}
            />
          );
          return tooltipMessage ? (
            <Tooltip title={tooltipMessage} arrow>
              <span>{completeButton}</span>
            </Tooltip>
          ) : (
            completeButton
          );
        })()}
    </Box>
  );
}
